package com.kaihuoy.home.screens

import androidx.compose.runtime.*
import com.kaihuoy.components.common.*
import com.kaihuoy.shared.data.PainekoeRecord
import com.kaihuoy.shared.data.PaineenNosto
import com.kaihuoy.shared.data.User
import com.kaihuoy.web.FirebaseService
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import kotlin.js.Date

@Composable
fun NewPainekoeScreen(user: User?, onBack: (() -> Unit)? = null) {
    val scope = rememberCoroutineScope()
    val today = Date().let {
        "${it.getDate().toString().padStart(2, '0')}.${(it.getMonth() + 1).toString().padStart(2, '0')}.${it.getFullYear()}"
    }

    var rakennusTiedot by remember { mutableStateOf("") }
    var tilaaja by remember { mutableStateOf("") }
    var puhelin by remember { mutableStateOf("") }
    var katuosoite by remember { mutableStateOf("") }
    var postinumero by remember { mutableStateOf("") }
    var jarjestelmanKuvaus by remember { mutableStateOf("") }

    var lahtopaine by remember { mutableStateOf("") }
    var nostetaanPaine by remember { mutableStateOf("") }
    var vedenMaaraAlussa by remember { mutableStateOf("") }
    var pidetaanPaine by remember { mutableStateOf("") }
    var paineenNostot by remember { mutableStateOf<List<PaineenNosto>>(emptyList()) }
    var uusiNosto by remember { mutableStateOf("") }
    var lasketaanPaine by remember { mutableStateOf("") }
    var vedenMaaraUlos by remember { mutableStateOf("") }
    var loppullinenVedenMaaraUlos by remember { mutableStateOf("") }

    var osio3 by remember { mutableStateOf("") }
    var huomautuksia by remember { mutableStateOf("") }
    var kokeenSuorittaja by remember { mutableStateOf(user?.displayName ?: "") }
    var tilaajanEdustaja by remember { mutableStateOf("") }

    var isSaving by remember { mutableStateOf(false) }

    PageLayout(greeting = getGreeting(user?.displayName), onLogout = {}) {
        Div(attrs = {
            style {
                padding(20.px, 0.px)
                width(100.percent)
                boxSizing("border-box")
            }
        }) {
            if (onBack != null) {
                Div(attrs = {
                    classes("no-print")
                    style { marginBottom(16.px) }
                }) {
                    SmallButton(
                        text = "← Takaisin",
                        onClick = onBack
                    )
                }
            }

            H1(attrs = {
                classes("no-print")
                style { color(KaihuTheme.TextPrimary); marginBottom(8.px) }
            }) { Text("Koeponnistuspöytäkirja") }

            P(attrs = {
                classes("no-print")
                style { color(KaihuTheme.TextSecondary); marginBottom(32.px) }
            }) { Text("Täytä kaikki kentät ja tallenna lomake. Aika kirjataan automaattisesti paineen nostojen yhteydessä.") }

            // ── Perustiedot ──────────────────────────────────────
            SectionCard(title = "Perustiedot") {
                TwoColGrid {
                    ReadonlyField("Koeponnistuksen tekijä", user?.displayName ?: "—")
                    ReadonlyField("Asennus pvm", today)
                }
            }

            // ── 1. Kohteen tiedot ─────────────────────────────────
            SectionCard(title = "1. Kohteen tiedot") {
                StyledInput(
                    label = "Rakennuksen / järjestelmän tiedot",
                    value = rakennusTiedot,
                    onValueChange = { rakennusTiedot = it },
                    placeholder = "Esim. Asuinrakennus, vesijohtoverkosto"
                )
                StyledInput(
                    label = "Tilaaja / Rakentaja",
                    value = tilaaja,
                    onValueChange = { tilaaja = it }
                )
                TwoColGrid {
                    StyledInput(
                        label = "Puhelin / sähköposti",
                        value = puhelin,
                        onValueChange = { puhelin = it }
                    )
                    StyledInput(
                        label = "Katuosoite",
                        value = katuosoite,
                        onValueChange = { katuosoite = it }
                    )
                }
                StyledInput(
                    label = "Postinumero / paikkakunta",
                    value = postinumero,
                    onValueChange = { postinumero = it }
                )
                StyledInput(
                    label = "Koeponnistettavan järjestelmän kuvaus",
                    value = jarjestelmanKuvaus,
                    onValueChange = { jarjestelmanKuvaus = it }
                )
            }

            // ── 2. Koeponnistustiedot ─────────────────────────────
            SectionCard(title = "2. Koeponnistustiedot") {
                TwoColGrid {
                    StyledInput(
                        label = "Lähtöpaine (bar)",
                        value = lahtopaine,
                        onValueChange = { lahtopaine = it },
                        placeholder = "esim. 2.5"
                    )
                    StyledInput(
                        label = "Nostetaan paine (bar)",
                        value = nostetaanPaine,
                        onValueChange = { nostetaanPaine = it },
                        placeholder = "esim. 7.5"
                    )
                }
                TwoColGrid {
                    StyledInput(
                        label = "Veden määrä alussa (l)",
                        value = vedenMaaraAlussa,
                        onValueChange = { vedenMaaraAlussa = it }
                    )
                    StyledInput(
                        label = "Pidetään paine (min)",
                        value = pidetaanPaine,
                        onValueChange = { pidetaanPaine = it },
                        placeholder = "esim. 30"
                    )
                }

                // Paineennostot
                Div(attrs = {
                    style {
                        marginTop(20.px)
                        property("border", "1px solid ${KaihuTheme.Border}")
                        borderRadius(12.px)
                        overflow("hidden")
                    }
                }) {
                    Div(attrs = {
                        style {
                            display(DisplayStyle.Flex)
                            justifyContent(JustifyContent.SpaceBetween)
                            alignItems(AlignItems.Center)
                            padding(12.px, 16.px)
                            backgroundColor(KaihuTheme.Surface)
                            property("border-bottom", "1px solid ${KaihuTheme.Border}")
                        }
                    }) {
                        Span(attrs = { style { fontWeight("600"); color(KaihuTheme.TextPrimary); fontSize(0.9.em) } }) {
                            Text("Paineen nostokirjaukset")
                        }
                        Span(attrs = { style { fontSize(0.8.em); color(KaihuTheme.TextSecondary) } }) {
                            Text("Kellonaika kirjataan automaattisesti")
                        }
                    }

                    if (paineenNostot.isEmpty()) {
                        Div(attrs = {
                            style {
                                padding(16.px)
                                color(KaihuTheme.TextSecondary)
                                fontSize(0.88.em)
                                textAlign("center")
                                fontStyle("italic")
                            }
                        }) { Text("Ei nostokirjauksia vielä.") }
                    } else {
                        Div(attrs = {
                            style {
                                display(DisplayStyle.Grid)
                                property("grid-template-columns", "80px 1fr 1fr auto")
                                padding(8.px, 16.px)
                                property("border-bottom", "1px solid ${KaihuTheme.Divider}")
                            }
                        }) {
                            listOf("#", "Kellonaika", "Veden määrä (l)", "").forEach { h ->
                                Span(attrs = {
                                    style {
                                        fontSize(0.75.em); fontWeight("700"); color(KaihuTheme.TextLight)
                                        property("text-transform", "uppercase"); property("letter-spacing", "0.05em")
                                    }
                                }) { Text(h) }
                            }
                        }
                        paineenNostot.forEachIndexed { idx, nosto ->
                            Div(attrs = {
                                style {
                                    display(DisplayStyle.Grid)
                                    property("grid-template-columns", "80px 1fr 1fr auto")
                                    padding(10.px, 16.px)
                                    alignItems(AlignItems.Center)
                                    if (idx % 2 == 0) backgroundColor(Color.transparent)
                                    else backgroundColor(KaihuTheme.CardBackground)
                                    property("border-bottom", "1px solid ${KaihuTheme.Divider}")
                                }
                            }) {
                                Span(attrs = { style { color(KaihuTheme.TextSecondary); fontSize(0.85.em) } }) { Text("${idx + 1}.") }
                                Span(attrs = { style { color(KaihuTheme.TextPrimary); fontSize(0.88.em) } }) { Text(nosto.aika) }
                                Span(attrs = { style { color(KaihuTheme.TextPrimary); fontSize(0.88.em) } }) { Text(nosto.vedenMaara) }
                                Span(attrs = {
                                    onClick {
                                        paineenNostot = paineenNostot.toMutableList().also { it.removeAt(idx) }
                                    }
                                    style {
                                        cursor("pointer"); color(KaihuTheme.Danger)
                                        fontSize(0.85.em); fontWeight("700")
                                        padding(2.px, 6.px)
                                    }
                                }) { Text("×") }
                            }
                        }
                    }

                    Div(attrs = {
                        style {
                            display(DisplayStyle.Flex)
                            gap(8.px)
                            alignItems(AlignItems.FlexEnd)
                            padding(12.px, 16.px)
                            backgroundColor(KaihuTheme.Background)
                        }
                    }) {
                        Div(attrs = { style { flex(1) } }) {
                            StyledInput(
                                label = "Lisättävä veden määrä (l)",
                                value = uusiNosto,
                                onValueChange = { uusiNosto = it },
                                placeholder = "esim. 0.5"
                            )
                        }
                        Div(attrs = { style { paddingBottom(2.px) } }) {
                            StyledButton(
                                text = "+ Lisää rivi",
                                backgroundColor = KaihuTheme.PrimaryTeal,
                                onClick = {
                                    if (uusiNosto.isBlank()) {
                                        AppFeedback.showWarning("Anna veden määrä ennen lisäystä.")
                                        return@StyledButton
                                    }
                                    val now = Date()
                                    val aika = "${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}"
                                    paineenNostot = paineenNostot + PaineenNosto(aika = aika, vedenMaara = uusiNosto.trim())
                                    uusiNosto = ""
                                }
                            )
                        }
                    }
                }

                Div(attrs = { style { marginTop(20.px) } }) {
                    TwoColGrid {
                        StyledInput(
                            label = "Lasketaan paine (bar)",
                            value = lasketaanPaine,
                            onValueChange = { lasketaanPaine = it }
                        )
                        StyledInput(
                            label = "Veden määrä ulos (l)",
                            value = vedenMaaraUlos,
                            onValueChange = { vedenMaaraUlos = it }
                        )
                    }
                    StyledInput(
                        label = "Loppullinen veden määrä ulos (l)",
                        value = loppullinenVedenMaaraUlos,
                        onValueChange = { loppullinenVedenMaaraUlos = it }
                    )
                }
            }

            // ── 3. Vapaa osio ─────────────────────────────────────
            SectionCard(title = "3. Lisätiedot") {
                StyledLargeInput(
                    label = "Lisätiedot / havainnot",
                    value = osio3,
                    onValueChange = { osio3 = it },
                    placeholder = "Kirjoita lisätiedot tähän..."
                )
            }

            // ── Huomautuksia ──────────────────────────────────────
            SectionCard(title = "Huomautuksia") {
                StyledLargeInput(
                    label = "Huomautuksia",
                    value = huomautuksia,
                    onValueChange = { huomautuksia = it },
                    placeholder = "Kirjoita huomautukset tähän..."
                )
            }

            // ── Allekirjoitukset ──────────────────────────────────
            SectionCard(title = "Allekirjoitukset") {
                TwoColGrid {
                    StyledInput(
                        label = "Kokeen suorittajan allekirjoitus ja nimenselvennys",
                        value = kokeenSuorittaja,
                        onValueChange = { kokeenSuorittaja = it }
                    )
                    StyledInput(
                        label = "Tilaajan / rakentajan edustajan allekirjoitus ja nimenselvennys",
                        value = tilaajanEdustaja,
                        onValueChange = { tilaajanEdustaja = it }
                    )
                }
            }

            // ── Tallenna ──────────────────────────────────────────
            Div(attrs = {
                style {
                    display(DisplayStyle.Flex)
                    gap(12.px)
                    marginTop(32.px)
                    justifyContent(JustifyContent.FlexEnd)
                }
            }) {
                StyledButton(
                    text = if (isSaving) "Tallennetaan..." else "Tallenna pöytäkirja",
                    icon = if (isSaving) null else "save",
                    enabled = !isSaving,
                    backgroundColor = KaihuTheme.Green,
                    onClick = {
                        if (tilaaja.isBlank()) {
                            AppFeedback.showWarning("Täytä vähintään Tilaaja/Rakentaja-kenttä.")
                            return@StyledButton
                        }
                        isSaving = true
                        scope.launch {
                            try {
                                val ref = FirebaseService.database.reference("painekokeet").push()
                                val id = ref.key ?: Date.now().toString()
                                ref.setValue(
                                    PainekoeRecord(
                                        id = id,
                                        userId = user?.uid ?: "",
                                        userName = user?.displayName ?: "",
                                        asennusPvm = today,
                                        rakennusTiedot = rakennusTiedot.trim(),
                                        tilaaja = tilaaja.trim(),
                                        puhelin = puhelin.trim(),
                                        katuosoite = katuosoite.trim(),
                                        postinumero = postinumero.trim(),
                                        jarjestelmanKuvaus = jarjestelmanKuvaus.trim(),
                                        lahtopaine = lahtopaine.trim(),
                                        nostetaanPaine = nostetaanPaine.trim(),
                                        vedenMaaraAlussa = vedenMaaraAlussa.trim(),
                                        pidetaanPaine = pidetaanPaine.trim(),
                                        paineenNostot = paineenNostot,
                                        lasketaanPaine = lasketaanPaine.trim(),
                                        vedenMaaraUlos = vedenMaaraUlos.trim(),
                                        loppullinenVedenMaaraUlos = loppullinenVedenMaaraUlos.trim(),
                                        osio3 = osio3.trim(),
                                        huomautuksia = huomautuksia.trim(),
                                        kokeenSuorittaja = kokeenSuorittaja.trim(),
                                        tilaajanEdustaja = tilaajanEdustaja.trim(),
                                        timestamp = Date.now().toLong(),
                                        status = "completed"
                                    )
                                )
                                AppFeedback.showSuccess("Koeponnistuspöytäkirja tallennettu.")
                                rakennusTiedot = ""; tilaaja = ""; puhelin = ""; katuosoite = ""
                                postinumero = ""; jarjestelmanKuvaus = ""; lahtopaine = ""
                                nostetaanPaine = ""; vedenMaaraAlussa = ""; pidetaanPaine = ""
                                paineenNostot = emptyList(); lasketaanPaine = ""; vedenMaaraUlos = ""
                                loppullinenVedenMaaraUlos = ""; osio3 = ""; huomautuksia = ""
                                kokeenSuorittaja = user?.displayName ?: ""; tilaajanEdustaja = ""
                            } catch (e: Exception) {
                                console.error("Painekokeen tallennus epäonnistui", e)
                                AppFeedback.showError("Tallennus epäonnistui: ${e.message}")
                            } finally {
                                isSaving = false
                            }
                        }
                    }
                )
            }
        }
    }
}

@Composable
private fun SectionCard(title: String, content: @Composable () -> Unit) {
    Div(attrs = {
        style {
            property("background", KaihuTheme.CardGradient)
            borderRadius(16.px)
            padding(if (isMobileScreen()) 16.px else 24.px)
            property("border", "1px solid ${KaihuTheme.Border}")
            property("box-shadow", KaihuTheme.ShadowSm)
            marginBottom(20.px)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(16.px)
        }
    }) {
        H3(attrs = {
            style {
                margin(0.px, 0.px, 4.px, 0.px)
                color(KaihuTheme.TextPrimary)
                fontSize(1.em)
                fontWeight("700")
            }
        }) { Text(title) }
        content()
    }
}

@Composable
private fun TwoColGrid(content: @Composable () -> Unit) {
    Div(attrs = {
        style {
            display(DisplayStyle.Grid)
            property("grid-template-columns", "repeat(auto-fill, minmax(min(280px, 100%), 1fr))")
            gap(16.px)
        }
    }) {
        content()
    }
}

@Composable
private fun ReadonlyField(label: String, value: String) {
    Div {
        Div(attrs = {
            style {
                fontSize(0.82.em); fontWeight("600"); color(KaihuTheme.TextSecondary)
                marginBottom(6.px); property("text-transform", "uppercase"); property("letter-spacing", "0.04em")
            }
        }) { Text(label) }
        Div(attrs = {
            style {
                padding(10.px, 14.px)
                borderRadius(10.px)
                backgroundColor(KaihuTheme.Divider)
                color(KaihuTheme.TextPrimary)
                fontSize(0.95.em)
                fontWeight("500")
            }
        }) { Text(value) }
    }
}
